import { createFileRoute } from "@tanstack/react-router";
import { aabb3Center } from "geom/aabb3";
import { brepCopy, faceNew } from "geom/brep";
import { faceAABB3, facesAABB3 } from "geom/brep";
import { mat4Mult, mat4Rotate, mat4Translate } from "geom/mat4";
import { planeCopy, planeCopyReversed, planeMove, planeMoveAlongNormal } from "geom/plane";
import { renderDCEL, renderDCELEdges, renderDCELVerts } from "geom/polyclip/render";
import { BRepTrimTestCases } from "geom/polyclip/test/trimBRepWithPlaneTestCases";
import type { TrimBRepWithPlaneResult } from "geom/polyclip/trimBRepWithPlane";
import { trimBRepWithPlane } from "geom/polyclip/trimBRepWithPlane";
import { trimBRepWithPlanes } from "geom/polyclip/trimBRepWithPlanes";
import { polyLine2SetCW } from "geom/polyLine2";
import { rect2ByCenterWidthHeight } from "geom/rect2";
import { transformBRep } from "geom/transform";
import { defaultCameraSettings } from "gfx/CameraSettings";
import { debugPlaneToTHREE, faceToTHREE, polyLine2ToTHREE } from "gfx/convert";
import { EnvPreset } from "gfx/EnvPreset";
import { envPresetSceneLights } from "gfx/sceneLights";
import THREEViewer from "gfx/THREEViewer";
import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { DoubleSide, LineBasicMaterial, MeshBasicMaterial, MeshLambertMaterial } from "three";
import colors from "ui/styles/colors";
import Canvas from "./-Canvas";

function BRepTrimTestPage() {
  const [viewer, setViewer] = useState<THREEViewer | null>(null);
  const viewerContainer = useRef<HTMLDivElement>(null);
  const [variant, setVariant] = useState<"trim_single_plane" | "trim_multiple_planes">("trim_single_plane");
  const [testIndex, setTestIndex] = useState(0);
  const [planeNudge, setPlaneNudge] = useState(8);
  const [showSubject, setShowSubject] = useState(true);
  const [fillResult, setFillResult] = useState(true);
  const [showCutPlane, setShowCutPlane] = useState(true);
  const [totalTime, setTotalTime] = useState<number>(0);
  const [averageTime, setAverageTime] = useState<number>(0);
  const [capDCEL, setCapDCEL] = useState<any>(null);
  const [explodeDistance, setExplodeDistance] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [translateZ, setTranslateZ] = useState(0);
  const [rotateZ, setRotateZ] = useState(0);
  const [parallelOffset, setParallelOffset] = useState(10);

  useEffect(() => {
    const viewerInstance = new THREEViewer({
      container: viewerContainer.current as HTMLDivElement,
      options: {
        gui: false,
        shadows: false,
        animate: true,
        showAxes: false,
        ssfxRenderer: true,
        debugFeaturesEnabled: false,
        sceneLights: envPresetSceneLights[EnvPreset.WHITEBOX_LOW_FOG],
        cameraSettings: {
          ...defaultCameraSettings,
          targetX: 0,
          targetY: 0,
          targetZ: 0,
          x: 500,
          y: -500,
          z: 500,
        },
      },
    });
    viewerInstance.controls.maxPolarAngle = Math.PI;
    viewerInstance.controls.update();

    setViewer(viewerInstance);

    return () => {
      viewerInstance.dispose();
      setViewer(null);
    };
  }, []);

  useEffect(() => {
    if (!viewer) {
      return undefined;
    }

    const cutPlane = planeMoveAlongNormal(planeCopy(BRepTrimTestCases[testIndex].cutPlane), planeNudge);
    const c = BRepTrimTestCases[testIndex];

    // Transform the BRep using translation and rotation
    const rotate = mat4Rotate(rotateZ / (2 * Math.PI), 0, 0, 1);
    const translate = mat4Translate(translateX, translateY, translateZ);
    const transform = rotate ? mat4Mult(translate, rotate) : translate;

    const transformedBRep = transformBRep(brepCopy(c.brep), transform);

    if (showSubject) {
      for (const face of transformedBRep.faces) {
        for (const loop of face.loops) {
          viewer.add(polyLine2ToTHREE({ path: loop, plane: face.plane }));
        }
      }
    }

    if (showCutPlane) {
      viewer.add(
        faceToTHREE(
          faceNew([polyLine2SetCW(rect2ByCenterWidthHeight(0, 0, 200, 200))], cutPlane),
          new MeshBasicMaterial({
            color: colors.blue,
            transparent: true,
            opacity: 0.2,
            side: DoubleSide,
          }),
        ),
      );

      viewer.add(debugPlaneToTHREE(cutPlane));
    }

    const count = 100;
    const t0 = performance.now();
    for (let i = 0; i < count; i++) {
      if (variant === "trim_single_plane") {
        trimBRepWithPlane(transformedBRep, cutPlane);
      } else {
        trimBRepWithPlanes(transformedBRep, [cutPlane]);
      }
    }
    const t1 = performance.now();
    const total = t1 - t0;
    const average = total / count;

    setTotalTime(total);
    setAverageTime(average);

    let res: Pick<TrimBRepWithPlaneResult, "allFaces"> | undefined;

    if (variant === "trim_single_plane") {
      const r = trimBRepWithPlane(transformedBRep, cutPlane);
      res = r;
      setCapDCEL(r.capDCEL);
    } else if (variant === "trim_multiple_planes") {
      const f = trimBRepWithPlanes(transformedBRep, [
        cutPlane,
        planeMoveAlongNormal(planeCopyReversed(cutPlane), parallelOffset),
      ]);

      res = {
        allFaces: f.flatMap((x) => x.faces),
      };
    }

    if (!res || res.allFaces.length === 0) {
      return () => {
        viewer.scene.clear();
      };
    }

    const allFaceAABB3 = facesAABB3(res.allFaces);
    const [cx, cy, cz] = aabb3Center(allFaceAABB3);

    for (const face of res.allFaces) {
      if (fillResult) {
        const [fx, fy, fz] = aabb3Center(faceAABB3(face));
        viewer.add(
          faceToTHREE(
            {
              ...face,
              plane: planeMove(
                planeCopy(face.plane),
                (fx - cx) * explodeDistance,
                (fy - cy) * explodeDistance,
                (fz - cz) * explodeDistance,
              ),
            },
            new MeshLambertMaterial({
              color: colors.green,
              transparent: true,
              opacity: 1,
              side: DoubleSide,
            }),
          ),
        );
      }

      for (const loop of face.loops) {
        viewer.add(
          polyLine2ToTHREE({
            path: loop,
            plane: face.plane,
            material: new LineBasicMaterial({
              color: "black",
              polygonOffset: true,
            }),
          }),
        );
      }
    }

    return () => {
      viewer.scene.clear();
    };
  }, [
    viewer,
    testIndex,
    parallelOffset,
    variant,
    planeNudge,
    showSubject,
    fillResult,
    showCutPlane,
    explodeDistance,
    translateX,
    translateY,
    translateZ,
    rotateZ,
  ]);

  const renderDCELCanvas = useCallback(
    (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D, scale: number) => {
      if (!capDCEL) {
        return;
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.save();

      // Get the bounding box of the DCEL vertices
      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      for (const vertex of capDCEL.vertices) {
        minX = Math.min(minX, vertex[0]);
        minY = Math.min(minY, vertex[1]);
        maxX = Math.max(maxX, vertex[0]);
        maxY = Math.max(maxY, vertex[1]);
      }

      const PADDING = 10;
      minX -= PADDING;
      minY -= PADDING;
      maxX += PADDING;
      maxY += PADDING;

      const width = maxX - minX;
      const height = maxY - minY;

      const scaleFactor = Math.min(canvas.width / width, canvas.height / height);
      const ratio = window.devicePixelRatio;
      const ss = scaleFactor / ratio;

      ctx.translate(-ss * minX, canvas.height / ratio + ss * minY);
      ctx.scale(ss, -ss);

      renderDCEL(ctx, capDCEL);
      renderDCELEdges(ctx, capDCEL, scale);
      renderDCELVerts(ctx, capDCEL, scale);

      ctx.restore();
    },
    [capDCEL],
  );

  return (
    <>
      <Head>
        <style>{`
          fieldset div {
            height: 2em;
            display: flex;
            flex-direction: row;
            align-items: center;
          }

          fieldset div label {
            width: 8em;
            text-align: right;
            padding-right: 1em;
          }

          .performance-metrics {
            margin-top: 1em;
            padding-top: 1em;
            border-top: 1px solid #ccc;
          }

          .performance-metrics div {
            color: #666;
          }
        `}</style>
      </Head>
      <div
        style={{
          padding: 5,
          border: "1px solid black",
          position: "absolute",
          top: 0,
          right: 0,
          width: 500,
          background: "white",
          zIndex: 100,
        }}
      >
        <Canvas canvasWidth={500} canvasHeight={500} render={renderDCELCanvas} />
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 100,
        }}
        ref={viewerContainer}
      >
        <fieldset style={{ display: "flex", flexDirection: "column" }}>
          <div>
            <label htmlFor="testCase">Test case</label>
            <select
              name="testCase"
              value={testIndex}
              onChange={(x) => {
                setTestIndex(parseInt(x.target.value));
                setPlaneNudge(0);
              }}
            >
              {BRepTrimTestCases.map((c, i) => (
                <option key={i} value={i}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label>Clip using</label>
            <div style={{ display: "flex", gap: "1em", alignItems: "center", marginLeft: "1em" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                <input
                  type="radio"
                  name="variant"
                  value="trim_single_plane"
                  checked={variant === "trim_single_plane"}
                  onChange={(e) => setVariant(e.target.value as "trim_single_plane" | "trim_multiple_planes")}
                />
                <span>Single Plane</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                <input
                  type="radio"
                  name="variant"
                  value="trim_multiple_planes"
                  checked={variant === "trim_multiple_planes"}
                  onChange={(e) => setVariant(e.target.value as "trim_single_plane" | "trim_multiple_planes")}
                />
                <span>Multiple Planes</span>
              </div>
            </div>
          </div>

          {variant === "trim_multiple_planes" && (
            <div>
              <label htmlFor="parallelOffset">Parallel Offset</label>
              <input
                name="parallelOffset"
                type="range"
                step={0.001}
                min={1}
                max={200}
                value={parallelOffset}
                onChange={(x) => setParallelOffset(parseFloat(x.target.value))}
              />
              <input
                type="number"
                value={parallelOffset}
                min={1}
                max={200}
                step={0.001}
                onChange={(e) => setParallelOffset(parseFloat(e.target.value))}
                style={{
                  width: "80px",
                  padding: "5px",
                  borderRadius: "4px",
                  border: "1px solid #ccc",
                }}
              />
            </div>
          )}

          <div>
            <label htmlFor="testCase">Plane nudge</label>
            <input
              name="planeNudge"
              type="range"
              step={1}
              min={-200}
              max={200}
              value={planeNudge}
              onChange={(x) => setPlaneNudge(parseFloat(x.target.value))}
            />
            <input
              style={{ width: "4em", marginLeft: "0.5em", padding: 0, textAlign: "center" }}
              type="number"
              value={planeNudge}
              onChange={(x) => setPlaneNudge(parseFloat(x.target.value))}
            ></input>
          </div>

          <div>
            <label htmlFor="showSubject">Show subject</label>
            <input
              name="showSubject"
              type="checkbox"
              checked={showSubject}
              onChange={() => setShowSubject(!showSubject)}
            />
          </div>

          <div>
            <label htmlFor="showCutPlane">Show cut plane</label>
            <input
              name="showCutPlane"
              type="checkbox"
              checked={showCutPlane}
              onChange={() => setShowCutPlane(!showCutPlane)}
            />
          </div>

          <div>
            <label htmlFor="fillResult">Fill result</label>
            <input name="fillResult" type="checkbox" checked={fillResult} onChange={() => setFillResult(!fillResult)} />
          </div>

          <div>
            <label htmlFor="explodeDistance">Explode result distance</label>
            <input
              name="explodeDistance"
              type="range"
              step={0.001}
              min={0}
              max={50}
              value={explodeDistance}
              onChange={(x) => setExplodeDistance(parseFloat(x.target.value))}
            />
          </div>

          <div>
            <label htmlFor="translateX">Translate X</label>
            <input
              name="translateX"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateX}
              onChange={(x) => setTranslateX(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateX}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateX(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="translateY">Translate Y</label>
            <input
              name="translateY"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateY}
              onChange={(x) => setTranslateY(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateY}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateY(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="translateZ">Translate Z</label>
            <input
              name="translateZ"
              type="range"
              step={0.001}
              min={-200}
              max={200}
              value={translateZ}
              onChange={(x) => setTranslateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={translateZ}
              min={-200}
              max={200}
              step={0.001}
              onChange={(e) => setTranslateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div>
            <label htmlFor="rotateZ">Rotate Z</label>
            <input
              name="rotateZ"
              type="range"
              step={0.001}
              min={-180}
              max={180}
              value={rotateZ}
              onChange={(x) => setRotateZ(parseFloat(x.target.value))}
            />
            <input
              type="number"
              value={rotateZ}
              min={-180}
              max={180}
              step={0.001}
              onChange={(e) => setRotateZ(parseFloat(e.target.value))}
              style={{
                width: "80px",
                padding: "5px",
                borderRadius: "4px",
                border: "1px solid #ccc",
              }}
            />
          </div>

          <div className="performance-metrics">
            <div>
              <label>Total time (100x):</label>
              <span>{totalTime.toFixed(2)} ms</span>
            </div>
            <div>
              <label>Average time:</label>
              <span>{averageTime.toFixed(2)} ms</span>
            </div>
          </div>
        </fieldset>
      </div>
      <div
        style={{
          position: "absolute",
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
        ref={viewerContainer}
      />
    </>
  );
}

export const Route = createFileRoute("/debug/polyclip/brep-trim-test")({
  component: BRepTrimTestPage,
});
